import React, { useState } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { withPageProtection } from '@/lib/rbac/hoc'
import { PAGES } from '@/lib/rbac/constants'
import { MetricCard } from '@/components/MetricCard'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { Badge } from '@/components/ui/Badge'
import { DepartmentCardSkeleton } from '@/components/common/DepartmentCardSkeleton'
import { useDepartments } from '@/hooks/useWorkforce'
import { DepartmentWithDetails } from '@/types/workforce'
import { Link } from '@tanstack/react-router'
import { SensitiveValue } from '@/components/privacy/SensitiveText'
import { usePageTitle } from '@/stores/page-title-store'
import {
  Building2,
  Plus,
  Search,
  Users,
  DollarSign,
  TrendingUp,
  MoreHorizontal,
  Edit,
  Eye,
  Trash2,
  RefreshCw
} from 'lucide-react'

function DepartmentsContent() {
  const { user } = useAuth()
  usePageTitle('Departamentos')

  // 🚀 Server State (TanStack Query)
  const {
    departments,
    loading,
    isFetching,
    error,
    refetch
  } = useDepartments()

  // 🎯 Local State (React useState for search)
  const [searchTerm, setSearchTerm] = useState('')

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(amount)
  }

  const filteredDepartments = departments.filter(dept => {
    if (searchTerm) {
      const search = searchTerm.toLowerCase()
      return (
        dept.name.toLowerCase().includes(search) ||
        dept.description?.toLowerCase().includes(search) ||
        dept.costCenter?.toLowerCase().includes(search)
      )
    }
    return true
  })

  const totalEmployees = departments.reduce((sum, dept) => sum + (dept.employeeCount || 0), 0)
  const totalBudget = departments.reduce((sum, dept) => {
    const budgetValue = typeof dept.budget === 'number' ? dept.budget : dept.budget?.allocated || 0
    return sum + budgetValue
  }, 0)
  const activeDepartments = departments.filter(dept => dept.status === "ACTIVE").length

  return (
    <div className="space-y-6">
        {/* Stats Cards */}
        <div className="dashboard-grid lg:grid-cols-4">
          <MetricCard
            title="Total de Departamentos"
            value={departments.length}
            icon={Building2}
            trend="neutral"
          />
          <MetricCard
            title="Departamentos Ativos"
            value={activeDepartments}
            change={`${Math.round((activeDepartments / departments.length) * 100)}%`}
            icon={TrendingUp}
            trend="up"
          />
          <MetricCard
            title="Total de Colaboradores"
            value={totalEmployees}
            icon={Users}
            trend="neutral"
          />
          <MetricCard
            title="Orçamento Total"
            value={
              <SensitiveValue mask="R$ ••••••">
                {formatCurrency(totalBudget)}
              </SensitiveValue>
            }
            icon={DollarSign}
            trend="neutral"
          />
        </div>

        {/* Search */}
        <Card className="p-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-brand-gray pointer-events-none z-10" />
            <Input
              placeholder="Buscar por nome, descrição ou centro de custo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              style={{ paddingLeft: '3rem' }}
            />
          </div>
        </Card>

        {/* 🚀 Background refresh indicator (subtle, non-blocking) */}
        {isFetching && !loading && (
          <div className="flex items-center gap-2 text-xs text-gray-400 bg-gray-800 px-3 py-1.5 rounded-md">
            <RefreshCw className="w-3 h-3 animate-spin" />
            <span>Sincronizando...</span>
          </div>
        )}

        {/* Departments List */}
        {loading ? (
          <DepartmentCardSkeleton cards={6} />
        ) : filteredDepartments.length === 0 ? (
          <EmptyState
            icon={Building2}
            title="Nenhum departamento encontrado"
            description="Não há departamentos que correspondam aos critérios de busca."
          />
        ) : (
          <div className="grid gap-6">
            {filteredDepartments.map((department) => (
              <Card key={department.departmentId} className="card-hover p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Link to={`/workforce/departments/${department.departmentId}` as any}>
                        <h3 className="font-display text-xl font-semibold text-white hover:text-brand-primary transition-colors">
                          {department.name}
                        </h3>
                      </Link>
                      <Badge className={department.status === "ACTIVE" ? 'bg-success/20 text-success' : 'bg-brand-gray/20 text-brand-gray'}>
                        {department.status === "ACTIVE" ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>

                    {department.description && (
                      <p className="text-brand-gray mb-4 max-w-3xl">
                        {department.description}
                      </p>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                      <div className="space-y-2">
                        <div className="text-sm text-brand-gray">Colaboradores</div>
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4 text-brand-primary" />
                          <span className="font-semibold text-white">
                            {department.employeeCount || 0}
                          </span>
                        </div>
                      </div>

                      {department.budget && (
                        <div className="space-y-2">
                          <div className="text-sm text-brand-gray">Orçamento</div>
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-brand-primary" />
                            <span className="font-semibold text-white">
                              <SensitiveValue mask="R$ ••••••">
                                {formatCurrency(typeof department.budget === 'number' ? department.budget : department.budget?.allocated || 0)}
                              </SensitiveValue>
                            </span>
                          </div>
                        </div>
                      )}

                      {department.costCenter && (
                        <div className="space-y-2">
                          <div className="text-sm text-brand-gray">Centro de Custo</div>
                          <div className="font-semibold text-white">
                            {department.costCenter}
                          </div>
                        </div>
                      )}

                      {department.manager && (
                        <div className="space-y-2">
                          <div className="text-sm text-brand-gray">Gerente</div>
                          <div className="font-semibold text-white">
                            {department.manager.personalInfo.name}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Roles and Employees Summary */}
                    <div className="mt-6 pt-4 border-t border-brand-gray/20">
                      <div className="flex flex-wrap gap-4">
                        {department.roles && department.roles.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-brand-gray">
                            <span>{department.roles.length} cargos</span>
                          </div>
                        )}
                        {department.employees && department.employees.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-brand-gray">
                            <span>
                              {department.employees.filter(emp => emp.status === 'ACTIVE').length} ativos
                            </span>
                          </div>
                        )}
                        {department.subDepartments && department.subDepartments.length > 0 && (
                          <div className="flex items-center gap-2 text-sm text-brand-gray">
                            <span>{department.subDepartments.length} subdepartamentos</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    <Link to={`/workforce/departments/${department.departmentId}` as any}>
                      <Button variant="ghost" size="sm">
                        <Eye className="w-4 h-4 mr-1" />
                        Ver
                      </Button>
                    </Link>
                    <Link to={`/workforce/departments/${department.departmentId}/edit` as any}>
                      <Button variant="ghost" size="sm">
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                    </Link>
                    <Button variant="ghost" size="sm">
                      <MoreHorizontal className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
  )
}

// Apply RBAC protection - only admin can access workforce departments
export default withPageProtection(DepartmentsContent, PAGES.WORKFORCE_DEPARTMENTS)